import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SITE from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { useSite, IMG } from '../components/StoreLayout';
import { STORE_BASE } from '../routeBase';
import { ProductCard } from './Home';
import { TestimonialScroller } from './AboutPage';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';

function ytVideoId(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? m[1] : '';
}

const CATEGORY = 'Power Swimming Goggles';
const EMPTY_FORM = {
  name: '', email: '', phone: '', message: '',
  od_sph: '', od_cyl: '', od_axis: '', od_add: '',
  os_sph: '', os_cyl: '', os_axis: '', os_add: '', pd: '',
};

export default function RxSwimGoggles() {
  const { content: c } = useSite();
  const portal = usePortal();
  const customer = portal?.customer;
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [mascotOffset, setMascotOffset] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  // Mascot drifts toward the cursor — subtle parallax, not a full follow, so
  // it still reads as "floating" rather than "chasing". Tracked on `window`
  // (not just the hero section) so it responds no matter where the cursor
  // is on the page, computed relative to the hero's current position.
  useEffect(() => {
    const onMove = e => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      const relX = (e.clientX - (rect.left + rect.width / 2)) / rect.width;
      const relY = (e.clientY - (rect.top + rect.height / 2)) / rect.height;
      const clamp = (v, m) => Math.max(-m, Math.min(m, v));
      setMascotOffset({ x: clamp(relX * 50, 26), y: clamp(relY * 40, 20) });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    // Logged-in customers get priced rows from the portal API (same pattern
    // as Home.js/Shop.js) — the public SITE endpoint never includes price.
    const client = customer ? PORTAL : SITE;
    client.get('/products', { params: { category: CATEGORY, limit: 12 } }).then(r => setProducts(r.data.products || []));
    SITE.get('/testimonials', { params: { page: 'rx-swimming-goggles' } }).then(r => setTestimonials(r.data.testimonials || []));
  }, [customer]);

  useEffect(() => {
    let slugs = [];
    try { slugs = JSON.parse(c['page.rx-swimming-goggles.featured_blogs'] || '[]'); } catch { /* ignore malformed content */ }
    if (!slugs.length) { setFeaturedBlogs([]); return; }
    SITE.get('/blogs').then(r => {
      const bySlug = new Map((r.data.blogs || []).map(b => [b.slug, b]));
      setFeaturedBlogs(slugs.map(s => bySlug.get(s)).filter(Boolean));
    });
  }, [c]);

  useSEO({
    title: c['page.rx-swimming-goggles.meta_title'] || c['page.rx-swimming-goggles.title'] || 'Rx Swimming Goggles',
    description: c['page.rx-swimming-goggles.meta_description'],
    canonical: `${window.location.origin}/store/page/rx-swimming-goggles`,
  });

  let benefits = [];
  try { benefits = JSON.parse(c['page.rx-swimming-goggles.benefits'] || '[]'); } catch { /* ignore malformed content */ }
  let faq = [];
  try { faq = JSON.parse(c['page.rx-swimming-goggles.faq'] || '[]'); } catch { /* ignore malformed content */ }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!form.name || !form.email) { setErr('Please enter your name and email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setErr('Please enter a valid email address');
    if (!form.phone || !form.phone.trim()) return setErr('Please enter your phone number');
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('captcha_token', captchaToken);
      if (file) fd.append('attachment', file);
      await axios.post('/api/rx-goggles/enquiry', fd);
      setSent(true);
      trackEvent('wholesale_enquiry', { source: 'rx-swimming-goggles' });
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not submit. Please try again.'); }
    setBusy(false);
  };

  const heroImg = c['page.rx-swimming-goggles.hero_image'] || c['homepage.home_banner_7'];
  const videoId = ytVideoId(c['page.rx-swimming-goggles.video_url']);

  return (
    <div style={{ position: 'relative' }}>
      <style>{`
        @keyframes rxFloat { 0%,100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-16px) rotate(3deg); } }
        @keyframes rxRipple { 0% { transform: scale(0.8); opacity: .5; } 100% { transform: scale(2.6); opacity: 0; } }
        @keyframes rxBubbleRise { 0% { transform: translateY(0) translateX(0); opacity: 0; } 10% { opacity: .9; } 90% { opacity: .6; } 100% { transform: translateY(-420px) translateX(24px); opacity: 0; } }
        .rx-mascot { animation: rxFloat 4s ease-in-out infinite; transition: transform .35s ease-out; }
        .rx-benefit-card { transition: transform .2s ease, box-shadow .2s ease; }
        .rx-benefit-card:hover { transform: translateY(-4px); box-shadow: 0 10px 30px rgba(0,0,0,0.08); }
        .rx-input { width:100%; padding:7px 9px; border:1.5px solid #e5e5e5; border-radius:6px; font-size:13px; box-sizing:border-box; text-align:center; }
        .rx-bubble { position:absolute; border-radius:50%; background:radial-gradient(circle at 30% 30%, rgba(255,255,255,0.98), rgba(133,205,233,0.45)); border: 1px solid rgba(255,255,255,0.8); box-shadow: 0 0 6px rgba(133,205,233,0.5); animation-name: rxBubbleRise; animation-timing-function: linear; animation-iteration-count: infinite; }
        /* Force every model card to the same height regardless of title
           length / swatch count, by stretching ProductCard's own internal
           chain (root div > Link > white card) down to the grid row's tallest
           card, without editing the shared component itself. */
        .rx-model-grid { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 24px; }
        .rx-model-card { width: 300px; flex: 0 0 auto; display: flex; }
        .rx-model-card > div { display: flex; flex-direction: column; width: 100%; }
        .rx-model-card > div > a { display: flex; flex-direction: column; flex: 1; }
        .rx-model-card > div > a > div { display: flex; flex-direction: column; flex: 1; }
        .rx-model-card > div > a > div > div:last-child { flex: 1; }
      `}</style>

      {/* Full-page water/pool backdrop — soft aqua gradient + ambient light
          pools + rising bubbles behind every section, so the whole page (not
          just the hero) reads as "underwater", while content cards stay on
          plain white for readability. */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none',
        background: `
          radial-gradient(circle at 15% 8%, rgba(133,205,233,0.35), transparent 38%),
          radial-gradient(circle at 85% 22%, rgba(133,205,233,0.28), transparent 42%),
          radial-gradient(circle at 25% 55%, rgba(133,205,233,0.22), transparent 40%),
          radial-gradient(circle at 80% 68%, rgba(133,205,233,0.26), transparent 40%),
          radial-gradient(circle at 40% 92%, rgba(133,205,233,0.3), transparent 40%),
          linear-gradient(180deg, #eaf6fb 0%, #f4fafd 30%, #eaf6fb 60%, #f4fafd 100%)
        `,
      }}>
        {/* Spread starting positions across the FULL page height (top: %),
            not just its bottom edge — otherwise, on a page this tall, bubbles
            rising a fixed distance from the very bottom never reach the
            sections near the top and the effect is invisible up there. */}
        {[...Array(28)].map((_, i) => (
          <span key={i} className="rx-bubble" style={{
            left: `${(i * 17 + 5) % 96}%`,
            top: `${(i * 137) % 100}%`,
            width: 10 + (i % 4) * 6, height: 10 + (i % 4) * 6,
            animationDuration: `${7 + (i % 5) * 1.8}s`,
            animationDelay: `${(i % 9) * 0.8}s`,
          }} />
        ))}
      </div>

      <div style={{ position: 'relative', zIndex: 1, paddingBottom: 60 }}>

      {/* HERO */}
      <section ref={heroRef} className="sf-fade-in" style={{
        position: 'relative', minHeight: 420, display: 'flex', alignItems: 'center', overflow: 'hidden',
        // Matches the 1600x700 "best size" hint given in the admin editor —
        // without this, a fluid-width section with only a minHeight floor
        // rarely has the same aspect ratio as the uploaded image, so
        // background-size:cover silently crops its top/bottom to fill
        // whatever height the box ends up with.
        ...(heroImg ? { aspectRatio: '1600 / 700' } : {}),
        background: heroImg
          ? `linear-gradient(180deg, rgba(8,35,52,.6), rgba(8,35,52,.8)), url(${IMG(heroImg)}) center/cover`
          : 'linear-gradient(160deg, #0a2838 0%, #124a63 45%, var(--sf-accent, #85cde9) 140%)',
      }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              position: 'absolute', left: `${18 + i * 32}%`, bottom: -40, width: 150, height: 150, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.3)', animation: `rxRipple ${5 + i}s ease-out ${i * 1.3}s infinite`,
            }} />
          ))}
        </div>

        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px', position: 'relative', display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ flex: '1 1 380px', color: '#fff' }}>
            <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.16)', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 16 }}>
              POWER SWIMMING GOGGLES
            </span>
            <h1 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 14px' }}>
              {c['page.rx-swimming-goggles.title'] || 'Rx Swimming Goggles'}
            </h1>
            <p style={{ fontSize: 16, opacity: 0.92, maxWidth: 480, lineHeight: 1.6, margin: 0 }}>
              {c['page.rx-swimming-goggles.subtitle'] || 'See every lap, every lane line, every wall — in your exact power.'}
            </p>
            <a href="#rx-form" style={{ display: 'inline-block', marginTop: 26, border: '1.5px solid #fff', color: '#fff', padding: '12px 30px', borderRadius: 26, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Submit Your Prescription
            </a>
          </div>
          <div style={{ flex: '0 0 100px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ transform: `translate(${mascotOffset.x}px, ${mascotOffset.y}px)`, transition: 'transform .35s ease-out' }}>
              <SwimmerMascot className="rx-mascot" width={95} height={126} />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / USAGE */}
      {c['page.rx-swimming-goggles'] && (
        <section style={{ maxWidth: 760, margin: '48px auto 0', padding: '0 24px', textAlign: 'center' }}>
          <div className="sf-richtext" style={{ fontSize: 15, lineHeight: 1.8, color: '#444' }} dangerouslySetInnerHTML={{ __html: c['page.rx-swimming-goggles'] }} />
        </section>
      )}

      {/* BENEFITS */}
      {benefits.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '40px auto 0', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {benefits.map((b, i) => (
              <div key={i} className="rx-benefit-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '26px 22px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'color-mix(in srgb, var(--sf-accent, #85cde9) 25%, white)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <i className={`ti ti-${b.icon}`} style={{ fontSize: 24, color: 'var(--sf-primary, #191919)' }} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{b.title}</h3>
                <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>{b.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MODELS */}
      {products.length > 0 && (
        <section style={{ maxWidth: 1300, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Our Models</h2>
          {c['page.rx-swimming-goggles.models_intro'] && (
            <p style={{ textAlign: 'center', color: '#777', fontSize: 14, maxWidth: 560, margin: '0 auto 28px' }}>{c['page.rx-swimming-goggles.models_intro']}</p>
          )}
          <div className="rx-model-grid">
            {products.map(p => (
              <div key={p.model_no} className="rx-model-card">
                <ProductCard p={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* VIDEO */}
      {videoId && (
        <section style={{ maxWidth: 900, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>See Them In The Water</h2>
          <div style={{ width: '100%', maxWidth: 720, height: 0, paddingTop: '56.25%', margin: '0 auto', position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1`}
              title="Power Swimming Goggles"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              allow="autoplay; encrypted-media" allowFullScreen
            />
          </div>
        </section>
      )}

      {/* RX PRESCRIPTION + CONTACT FORM */}
      <section id="rx-form" style={{ maxWidth: 720, margin: '56px auto 0', padding: '0 24px' }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 16, padding: 30 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <i className="ti ti-circle-check" style={{ fontSize: 44, color: '#16a34a' }} />
              <h3 style={{ marginTop: 10 }}>Thank you!</h3>
              <p style={{ color: '#666', fontSize: 14 }}>Your prescription has been received — our team will review it and get in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Get Fitted With Your Prescription</h3>
              <p style={{ fontSize: 13.5, color: '#888', marginBottom: 18 }}>Enter your lens power below, or simply attach a photo/scan of your prescription — either works.</p>
              {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '8px 11px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}

              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <FormField label="Name *" value={form.name} onChange={set('name')} required />
                <FormField label="Email *" type="email" value={form.email} onChange={set('email')} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Phone *</label>
                <StorePhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} required />
              </div>

              <h4 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>Lens Prescription (optional if attaching a scan)</h4>
              <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                  <thead>
                    <tr>{['', 'SPH', 'CYL', 'AXIS', 'ADD'].map(h => <th key={h} style={{ fontSize: 11, color: '#888', padding: '4px 6px', textAlign: 'center' }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    <RxRow label="OD (right)" prefix="od" form={form} set={set} />
                    <RxRow label="OS (left)" prefix="os" form={form} set={set} />
                  </tbody>
                </table>
              </div>
              <div style={{ maxWidth: 160, marginBottom: 16 }}>
                <label style={lbl}>PD (mm)</label>
                <input className="rx-input" value={form.pd} onChange={set('pd')} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Attach your prescription (photo or PDF, optional)</label>
                <input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
                {file && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>✓ {file.name}</div>}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Message</label>
                <textarea value={form.message} onChange={set('message')} rows={3}
                  style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>

              <Captcha onToken={setCaptchaToken} />
              <button type="submit" disabled={busy} className="sf-btn-outline"
                style={{ width: '100%', padding: 12, border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                {busy ? 'Submitting…' : 'Submit Prescription'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>What Swimmers Say</h2>
          <TestimonialScroller items={testimonials} />
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section style={{ maxWidth: 800, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>Frequently Asked Questions</h2>
          {faq.map((f, i) => (
            <details key={i} style={{ borderTop: '1px solid #eee', padding: '12px 0' }}>
              <summary style={{ fontSize: 14.5, fontWeight: 600, cursor: 'pointer', color: 'var(--sf-primary)' }}>{f.question}</summary>
              <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.7, marginTop: 8 }}>{f.answer}</p>
            </details>
          ))}
        </section>
      )}

      {/* BLOG */}
      <section style={{ maxWidth: 1100, margin: '56px auto 70px', padding: '0 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>Swimming &amp; Eyecare Tips</h2>
        {featuredBlogs.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
            {featuredBlogs.map(b => (
              <Link key={b.slug} to={`${STORE_BASE}/blog/${b.slug}`} style={{ textDecoration: 'none', color: 'inherit', width: 280, flex: '0 0 auto' }}>
                <div className="sf-journal-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', height: '100%' }}>
                  <div style={{ height: 150, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {b.cover_image ? <img src={IMG(b.cover_image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="ti ti-article" style={{ fontSize: 36, color: '#ddd' }} />}
                  </div>
                  <div style={{ padding: 14 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.35 }}>{b.title}</h3>
                    <p style={{ fontSize: 12.5, color: '#777', lineHeight: 1.6 }}>{b.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 20px', background: '#f7f8fa', borderRadius: 14, color: '#888' }}>
            <i className="ti ti-notes" style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
            More swimming &amp; eyecare tips coming soon.
          </div>
        )}
      </section>

      </div>
    </div>
  );
}

function RxRow({ label, prefix, form, set }) {
  return (
    <tr>
      <td style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 6px' }}>{label}</td>
      {['sph', 'cyl', 'axis', 'add'].map(f => (
        <td key={f} style={{ padding: '4px 6px' }}>
          <input className="rx-input" value={form[`${prefix}_${f}`]} onChange={set(`${prefix}_${f}`)} />
        </td>
      ))}
    </tr>
  );
}

function FormField({ label, value, onChange, type = 'text', required }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={lbl}>{label}</label>
      <input type={type} required={required} value={value} onChange={onChange} style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };

// Original flat-illustration diver/swimmer mascot (not traced from any
// third-party asset) — dark wetsuit + mask/flippers in the site's own
// --sf-primary/--sf-accent theme colours, one arm raised in a thumbs-up.
function SwimmerMascot({ className, width = 150, height = 200 }) {
  const primary = 'var(--sf-primary, #0e2a3d)';
  const accent = 'var(--sf-accent, #85cde9)';
  return (
    <svg className={className} width={width} height={height} viewBox="0 0 160 210" xmlns="http://www.w3.org/2000/svg">
      {/* bubbles */}
      <circle cx="104" cy="10" r="4" fill="#fff" opacity="0.75" />
      <circle cx="116" cy="2" r="2.5" fill="#fff" opacity="0.6" />
      {/* breathing tube */}
      <rect x="76" y="16" width="8" height="16" rx="3" fill={primary} />
      {/* head */}
      <circle cx="80" cy="40" r="20" fill={primary} />
      {/* mask */}
      <rect x="58" y="32" width="44" height="16" rx="8" fill="#fff" opacity="0.92" />
      <circle cx="68" cy="40" r="7" fill={accent} />
      <circle cx="92" cy="40" r="7" fill={accent} />
      {/* torso */}
      <rect x="55" y="58" width="50" height="70" rx="22" fill={primary} />
      <rect x="55" y="86" width="50" height="14" fill={accent} />
      {/* raised right arm + thumbs-up hand */}
      <rect x="95" y="55" width="14" height="50" rx="7" fill={primary} transform="rotate(-50 102 60)" />
      <circle cx="127" cy="34" r="9" fill="#f2c299" />
      <rect x="123" y="20" width="6" height="14" rx="3" fill="#f2c299" transform="rotate(-10 126 27)" />
      {/* left arm at side */}
      <rect x="50" y="60" width="14" height="45" rx="7" fill={primary} transform="rotate(15 57 62)" />
      <circle cx="47" cy="103" r="8" fill="#f2c299" />
      {/* legs in flutter-kick pose */}
      <rect x="60" y="120" width="16" height="60" rx="8" fill={primary} transform="rotate(-12 68 120)" />
      <rect x="92" y="120" width="16" height="60" rx="8" fill={primary} transform="rotate(15 100 120)" />
      {/* flippers */}
      <ellipse cx="53" cy="186" rx="17" ry="8" fill={accent} transform="rotate(-20 53 186)" />
      <ellipse cx="119" cy="189" rx="17" ry="8" fill={accent} transform="rotate(15 119 189)" />
    </svg>
  );
}
